import { AppThunkAction } from 'cityads-v3/types/reducer';

interface FormErrors {
  [fieldName: string]: string[]
}

export interface GenericErrorResponse {
  error: string
}

export interface ServerErrorResponse {
  errors: {
    message: string
  }
}

export interface FormErrorResponse {
  form_errors: FormErrors
}

export type ErrorResponse = GenericErrorResponse | ServerErrorResponse | FormErrorResponse

function isServerErrorResponse(r: ErrorResponse): r is ServerErrorResponse {
  return !!(r as ServerErrorResponse).errors?.message
}

function isFormErrorResponse(r: ErrorResponse): r is FormErrorResponse {
  return !!(r as FormErrorResponse).form_errors
}

export class FormError extends Error {
  errors: FormErrors

  constructor(formErrors: FormErrors) {
    const message = Object.keys(formErrors)
      .reduce((errorStrings: string[], fieldName: string) => [
        ...errorStrings,
        `${fieldName}: ${formErrors[fieldName].join(', ')}`
      ], [])
      .join('; ')
    ;

    super(message);

    this.errors = formErrors;

    Object.setPrototypeOf(this, FormError.prototype);
  }
}

type ErrorDataFromRequest = { data: ErrorResponse }

function isErrorDataFromRequest(r: unknown): r is ErrorDataFromRequest {
  return !!(r as ErrorDataFromRequest).data
}

export function requestWrapper<ResponseType, ResultType = ResponseType>(
  requestFunction: (...args: unknown[]) => Promise<ResponseType>,
  normalizeResponse: (response: ResponseType) => ResultType,
): AppThunkAction<Promise<ResultType>> {
  return async function (...args: unknown[]): Promise<ResultType> {
    try {
      return normalizeResponse((await requestFunction(...args)))
    } catch (e) {
      if (isErrorDataFromRequest(e)) {
        const errorResponse: ErrorResponse = e.data;

        if (isFormErrorResponse(errorResponse)) {
          throw new FormError(errorResponse.form_errors);
        } else if (isServerErrorResponse(errorResponse)) {
          throw new Error(errorResponse.errors.message);
        } else {
          throw new Error(errorResponse.error);
        }
      } else {
        throw e;
      }
    }
  }
}
